import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SimilarReader {
  id: string;
  username: string;
  name: string;
  image?: string;
  image_thumb?: string;
  similarity_score: number;
  match_reason: string;
  shared_books_count: number;
  shared_authors_count: number;
  shared_tags_count: number;
  isFollowing?: boolean;
  recentReviews?: Array<{
    id: string;
    book_id: string;
    rating_star: number;
    review_text?: string;
    created_at: string;
    books: {
      id: string;
      title: string;
      authors: string[];
      cover_url?: string;
      cover_cache_url?: string;
    };
  }>;
}

export const useSimilarReaders = () => {
  return useQuery({
    queryKey: ['similarReaders'],
    queryFn: async (): Promise<SimilarReader[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get similar readers using enhanced algorithm
      const { data: similarReaders, error: similarError } = await supabase
        .rpc('get_enhanced_similar_readers', {
          target_user_id: user.id,
          result_limit: 5
        });

      if (similarError) throw similarError;
      if (!similarReaders || similarReaders.length === 0) return [];

      const userIds = similarReaders.map((r: any) => r.id);

      // Fetch follow status
      const { data: follows } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .in('following_id', userIds);

      const followingIds = new Set(follows?.map(f => f.following_id) || []);

      // Fetch recent reviews from similar readers
      const { data: recentReviews } = await supabase
        .from('user_books')
        .select(`
          id,
          book_id,
          user_id,
          rating_star,
          review_text,
          created_at,
          books (
            id,
            title,
            authors,
            cover_url,
            cover_cache_url
          )
        `)
        .in('user_id', userIds)
        .eq('privacy', 'public')
        .not('rating_star', 'is', null)
        .order('created_at', { ascending: false })
        .limit(15);

      // Group reviews by user
      const reviewsByUser = new Map<string, any[]>();
      recentReviews?.forEach(review => {
        const userReviews = reviewsByUser.get(review.user_id) || [];
        userReviews.push(review);
        reviewsByUser.set(review.user_id, userReviews);
      });

      // Combine data
      return similarReaders.map((reader: any) => ({
        ...reader,
        isFollowing: followingIds.has(reader.id),
        recentReviews: (reviewsByUser.get(reader.id) || []).slice(0, 1), // Show most recent review
      }));
    },
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
